import { Window, withFormProps } from '@kizi/comparator-components';
import {
  FilterQueryTriplePlay,
  generateFiltersTriplePlay,
  useField,
  useFormService,
  useWizardService,
} from '@kizi/comparator-core';
import { Div, Label, LabelProps, P } from '@kizi/core-components';
import { Checkbox } from '@kizi/shared-components';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

interface ContentTypeProps {
  _fieldName: string;
  partOfFilter: boolean;
  label: string;
  tooltip?: string;
  labelProps?: LabelProps;
  errorType?: 'text' | 'popover';
}

const checkboxes = [
  { label: 'Internet', value: 'internet' },
  { label: 'Televisie', value: 'television' },
  { label: 'Bellen (vast)', value: 'call' },
];

const query = {
  category: 'general',
  name: 'productTypes',
};

const FormCheckbox = withFormProps(Checkbox, {
  _containerProps: {
    variant: 'formGroupInlineReversed',
    alignContent: 'center',
  },
});

const LabelWithTooltip = withFormProps(Label);

export const ContentTypeCheckbox: FC<ContentTypeProps> = ({
  _fieldName,
  partOfFilter = false,
  label,
  tooltip,
  labelProps,
  errorType = 'popover',
}) => {
  const formService = useFormService();
  const wizardService = useWizardService();
  const service = useMemo(
    () => formService || wizardService,
    [formService, wizardService],
  );
  const field = useField(_fieldName, service);
  const filtersField = useField('overview.filters', service);
  const defaultFiltersField = useField('overview.defaultFilters', service);
  const filterGraphQL = useField('overview.filterGraphQL', service);
  const divRef = useRef<HTMLDivElement>(null);
  const isMobile = window.matchMedia('(max-width: 767px)').matches;

  const filtersFieldValues = useMemo(
    () => JSON.parse(filtersField?.value as string),
    [filtersField?.value],
  );

  const defaultFiltersFieldValues = useMemo(
    () => JSON.parse(defaultFiltersField?.value as string),
    [defaultFiltersField?.value],
  );

  const filterGraphQLValues: FilterQueryTriplePlay[] = useMemo(
    () => JSON.parse(filterGraphQL?.value as string),
    [filterGraphQL?.value],
  );

  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    partOfFilter
      ? filtersFieldValues['productTypes']
      : JSON.parse(field?.value as string),
  );

  const [onlyCallOptionError, setOnlyCallOptionError] = useState(false);
  const [errorText, setErrorText] = useState('');

  const filterAndJoinOptions = (options: string[]) =>
    options
      .filter((option) => option !== 'combinationDiscount')
      .map((option) => option.charAt(0).toUpperCase() + option.slice(1))
      .join(', ');

  const handleChange = (value: string) => {
    if (errorText) {
      setErrorText('');
    }
    if (selectedOptions.includes(value)) {
      setSelectedOptions((p) => p.filter((option) => option !== value));
    } else {
      setSelectedOptions((p) => [...p, value]);
    }
  };

  const handleCallError = () => {
    setOnlyCallOptionError(!onlyCallOptionError);
    if (errorType === 'text') {
      setErrorText('Een abonnement met alleen bellen is niet mogelijk.');
    }
  };

  const checkIsDisabled = (value: string) => {
    return value === 'call' && selectedOptions.length === 1;
  };

  useEffect(() => {
    if (partOfFilter && filtersField?.value === defaultFiltersField?.value) {
      setSelectedOptions(filtersFieldValues['productTypes']);
    }
  }, [filtersFieldValues]);

  useEffect(() => {
    if (selectedOptions.includes('call') && selectedOptions.length === 2) {
      handleCallError();
      setSelectedOptions([]);
    }

    const augmentedOptions = [...selectedOptions];
    if (!augmentedOptions.includes('combinationDiscount')) {
      augmentedOptions.push('combinationDiscount');
    }
    if (partOfFilter) {
      service?.send([
        {
          type: 'CHANGE',
          target: {
            name: 'filters',
            value: JSON.stringify({
              ...filtersFieldValues,
              productTypes: augmentedOptions,
            }),
          },
        },
        {
          type: 'CHANGE',
          target: {
            name: 'filterGraphQL',
            value: JSON.stringify(
              generateFiltersTriplePlay(filterGraphQLValues, [
                {
                  ...query,
                  value: filterAndJoinOptions(augmentedOptions),
                },
              ]),
            ),
          },
        },
      ]);
      field?.set({
        value: JSON.stringify(augmentedOptions),
      });
    } else {
      field?.set({
        value: JSON.stringify(augmentedOptions),
      });
      filtersField?.set({
        value: JSON.stringify({
          ...filtersFieldValues,
          productTypes: augmentedOptions,
        }),
      });
      defaultFiltersField?.set({
        value: JSON.stringify({
          ...defaultFiltersFieldValues,
          productTypes: augmentedOptions,
        }),
      });
      filterGraphQL?.set({
        value: JSON.stringify(
          generateFiltersTriplePlay(filterGraphQLValues, [
            { ...query, value: filterAndJoinOptions(augmentedOptions) },
          ]),
        ),
      });
    }
  }, [selectedOptions]);

  if (!field || !filtersField || !defaultFiltersField) return null;

  return (
    <Div ref={divRef} variant="contentTypeCheckbox">
      <LabelWithTooltip
        _label={label}
        _labelProps={{
          color: field?.errors ? 'error' : '',
          mb: 0,
          ...labelProps,
        }}
        _tooltip={tooltip}
      />
      {checkboxes.map((option, index) => (
        <FormCheckbox
          key={`content-checkbox-${index}`}
          checked={selectedOptions.includes(option.value)}
          variant="main"
          _label={option.label}
          value={option.value}
          disabled={checkIsDisabled(option.value)}
          onChange={() => handleChange(option.value)}
          _errorProps={{ variant: 'error' }}
          _successProps={{ variant: 'success' }}
        />
      ))}
      {field?.errors ? (
        <P variant="footnoteError" mt={2}>
          {field.errors[0]}
        </P>
      ) : (
        <P variant="footnoteGrey" color="secondary.0" mt={2}>
          {errorText}
        </P>
      )}
      {errorType === 'popover' && (
        <Window
          show={onlyCallOptionError}
          type={isMobile ? 'layover' : 'popover'}
          onClose={handleCallError}
          popoverProps={{
            attachTo: divRef.current,
          }}
          title="Selecteer meer opties"
        >
          <Div p={3}>
            <P variant="baseBodyImportant" mb={3} textAlign="center">
              Een abonnement met alleen bellen is niet mogelijk.
            </P>
          </Div>
        </Window>
      )}
    </Div>
  );
};
